import React, { useEffect, useState, useCallback } from "react";
import { api, BRL } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, CheckCircle2, XCircle, Star, Trash2, Ban, ShieldCheck,
  Building2, Briefcase, CreditCard, Clock, Inbox,
} from "lucide-react";

const Tab = ({ active, onClick, children, testId, count }) => (
  <button data-testid={testId} onClick={onClick}
    className={`px-5 py-2.5 rounded-xl font-extrabold transition-colors flex items-center gap-2 ${active ? "bg-slate-950 text-white" : "bg-white text-slate-700 border border-slate-200 hover:bg-slate-50"}`}>
    {children}
    {count > 0 && <span className="bg-emerald-400 text-slate-950 text-xs px-2 py-0.5 rounded-full">{count}</span>}
  </button>
);

export default function AdminDashboard() {
  const [tab, setTab] = useState("jobs");
  const [jobs, setJobs] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [payments, setPayments] = useState([]);
  const [requests, setRequests] = useState({ job_requests: [], profile_requests: [] });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [j, c, p, r] = await Promise.all([
        api.get("/admin/jobs"), api.get("/admin/companies"), api.get("/admin/payments"), api.get("/admin/requests"),
      ]);
      setJobs(j.data); setCompanies(c.data); setPayments(p.data); setRequests(r.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    await api.post(`/admin/jobs/${id}/status`, { status });
    toast.success(status === "approved" ? "Vaga aprovada! 30 dias começam agora." : "Vaga reprovada.");
    load();
  };
  const toggleFeature = async (j) => {
    await api.post(`/admin/jobs/${j.id}/feature`, { featured: !j.featured });
    load();
  };
  const deleteJob = async (id) => {
    if (!window.confirm("Excluir vaga?")) return;
    await api.delete(`/admin/jobs/${id}`); toast.success("Vaga excluída."); load();
  };
  const approveCompany = async (id, approved) => {
    await api.post(`/admin/companies/${id}/approve`, { approved });
    toast.success(approved ? "Empresa aprovada!" : "Aprovação removida."); load();
  };
  const blockCompany = async (id, blocked) => {
    await api.post(`/admin/companies/${id}/block`, { blocked });
    toast.success(blocked ? "Empresa bloqueada. Vagas ocultadas do site." : "Empresa desbloqueada."); load();
  };
  const jobChange = async (id, action) => {
    await api.post(`/admin/jobs/${id}/change/${action}`);
    toast.success(action === "approve" ? "Alteração aplicada." : "Alteração recusada."); load();
  };
  const profileReq = async (id, action) => {
    await api.post(`/admin/profile-requests/${id}/${action}`);
    toast.success(action === "approve" ? "Dados aprovados." : "Solicitação recusada."); load();
  };

  const pendingJobs = jobs.filter((j) => j.status === "pending").length;
  const totalRequests = requests.job_requests.length + requests.profile_requests.length;

  return (
    <div className="max-w-7xl mx-auto px-5 py-12">
      <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-950 flex items-center gap-2">
        <ShieldCheck /> Painel do administrador
      </h1>
      <p className="text-slate-600 mt-1 mb-8">Aprove empresas e vagas, destaque anúncios e acompanhe pagamentos.</p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {[
          [Briefcase, "Vagas", jobs.length],
          [Clock, "Aguardando", pendingJobs],
          [Building2, "Empresas", companies.length],
          [CreditCard, "Pagamentos", payments.filter((p) => p.status === "paid").length],
        ].map(([Icon, label, val]) => (
          <div key={label} className="bg-white rounded-2xl p-5 border border-slate-200">
            <Icon size={18} className="text-slate-400 mb-2" />
            <strong className="font-heading text-2xl">{val}</strong>
            <p className="text-slate-500 font-bold text-sm">{label}</p>
          </div>
        ))}
      </div>

      <div className="flex gap-3 mb-8 flex-wrap">
        <Tab testId="tab-jobs" active={tab === "jobs"} onClick={() => setTab("jobs")}>Vagas</Tab>
        <Tab testId="tab-companies" active={tab === "companies"} onClick={() => setTab("companies")}>Empresas</Tab>
        <Tab testId="tab-requests" active={tab === "requests"} onClick={() => setTab("requests")} count={totalRequests}>Solicitações</Tab>
        <Tab testId="tab-payments" active={tab === "payments"} onClick={() => setTab("payments")}>Pagamentos</Tab>
      </div>

      {loading ? (
        <div className="flex justify-center py-20 text-slate-500"><Loader2 className="animate-spin" size={32} /></div>
      ) : tab === "jobs" ? (
        <div className="grid gap-4" data-testid="admin-jobs-list">
          {jobs.length === 0 && <p className="text-slate-500">Nenhuma vaga cadastrada.</p>}
          {jobs.map((j) => (
            <div key={j.id} className="bg-white rounded-2xl p-6 border border-slate-200 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  <span className={`text-xs font-extrabold px-3 py-1 rounded-full ${j.status === "approved" ? "bg-emerald-100 text-emerald-800" : j.status === "rejected" ? "bg-red-100 text-red-800" : j.status === "closed" ? "bg-slate-200 text-slate-700" : "bg-amber-100 text-amber-800"}`}>
                    {j.status === "approved" ? "Publicada" : j.status === "rejected" ? "Reprovada" : j.status === "closed" ? "Encerrada" : "Em análise"}
                  </span>
                  {j.featured && <span className="bg-emerald-100 text-emerald-800 text-xs font-extrabold px-3 py-1 rounded-full flex items-center gap-1"><Star size={12} /> Destaque</span>}
                  <span className="bg-blue-100 text-blue-800 text-xs font-extrabold px-3 py-1 rounded-full">{j.cidade}</span>
                </div>
                <h3 className="font-heading text-lg font-extrabold text-slate-950">{j.cargo}</h3>
                <p className="text-slate-600">{j.company_name} • {j.tipo} • {j.salario}</p>
              </div>
              <div className="flex gap-2 flex-wrap">
                {j.status !== "approved" && (
                  <button data-testid={`approve-job-${j.id}`} onClick={() => setStatus(j.id, "approved")}
                    className="bg-emerald-400 text-slate-950 font-bold px-3 py-2 rounded-lg flex items-center gap-1 hover:bg-emerald-500"><CheckCircle2 size={16} /> Aprovar</button>
                )}
                {j.status !== "rejected" && (
                  <button data-testid={`reject-job-${j.id}`} onClick={() => setStatus(j.id, "rejected")}
                    className="bg-slate-100 text-slate-800 font-bold px-3 py-2 rounded-lg flex items-center gap-1 hover:bg-slate-200"><XCircle size={16} /> Reprovar</button>
                )}
                <button data-testid={`feature-job-${j.id}`} onClick={() => toggleFeature(j)}
                  className="bg-slate-100 text-slate-800 font-bold px-3 py-2 rounded-lg flex items-center gap-1 hover:bg-slate-200"><Star size={16} /> {j.featured ? "Remover destaque" : "Destacar"}</button>
                <button data-testid={`admin-delete-job-${j.id}`} onClick={() => deleteJob(j.id)}
                  className="text-red-600 hover:bg-red-50 px-3 py-2 rounded-lg"><Trash2 size={16} /></button>
              </div>
            </div>
          ))}
        </div>
      ) : tab === "companies" ? (
        <div className="grid gap-4" data-testid="admin-companies-list">
          {companies.length === 0 && <p className="text-slate-500">Nenhuma empresa cadastrada.</p>}
          {companies.map((c) => (
            <div key={c.user_id} className="bg-white rounded-2xl p-6 border border-slate-200 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  {c.blocked ? <span className="bg-red-100 text-red-800 text-xs font-extrabold px-3 py-1 rounded-full">Bloqueada</span>
                    : c.approved ? <span className="bg-emerald-100 text-emerald-800 text-xs font-extrabold px-3 py-1 rounded-full">Aprovada</span>
                    : <span className="bg-amber-100 text-amber-800 text-xs font-extrabold px-3 py-1 rounded-full">Aguardando</span>}
                </div>
                <h3 className="font-heading text-lg font-extrabold text-slate-950">{c.company_name || c.name || "(sem cadastro)"}</h3>
                <p className="text-slate-600">{c.email} • {c.cidade || "—"} • {c.active_jobs} vaga(s) ativa(s)</p>
              </div>
              <div className="flex gap-2 flex-wrap">
                <button data-testid={`approve-company-${c.user_id}`} onClick={() => approveCompany(c.user_id, !c.approved)}
                  className="bg-emerald-400 text-slate-950 font-bold px-3 py-2 rounded-lg flex items-center gap-1 hover:bg-emerald-500">
                  <CheckCircle2 size={16} /> {c.approved ? "Reprovar" : "Aprovar"}
                </button>
                <button data-testid={`block-company-${c.user_id}`} onClick={() => blockCompany(c.user_id, !c.blocked)}
                  className="bg-slate-100 text-slate-800 font-bold px-3 py-2 rounded-lg flex items-center gap-1 hover:bg-slate-200">
                  <Ban size={16} /> {c.blocked ? "Desbloquear" : "Bloquear"}
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : tab === "requests" ? (
        <div className="grid gap-4" data-testid="admin-requests-list">
          {totalRequests === 0 && (
            <div className="bg-white rounded-3xl p-10 border border-slate-200 text-center text-slate-500">
              <Inbox size={32} className="mx-auto mb-2" /> Nenhuma solicitação pendente.
            </div>
          )}
          {requests.profile_requests.map((c) => (
            <div key={`p-${c.user_id}`} className="bg-white rounded-2xl p-6 border border-slate-200 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div>
                <span className="bg-blue-100 text-blue-800 text-xs font-extrabold px-3 py-1 rounded-full">Alteração de dados</span>
                <h3 className="font-heading text-lg font-extrabold text-slate-950 mt-2">{c.company_name} → {c.pending_profile?.company_name}</h3>
                <p className="text-slate-600">Cidade: {c.cidade} → {c.pending_profile?.cidade}</p>
              </div>
              <div className="flex gap-2">
                <button data-testid={`profile-approve-${c.user_id}`} onClick={() => profileReq(c.user_id, "approve")}
                  className="bg-emerald-400 text-slate-950 font-bold px-3 py-2 rounded-lg flex items-center gap-1 hover:bg-emerald-500"><CheckCircle2 size={16} /> Aprovar</button>
                <button data-testid={`profile-reject-${c.user_id}`} onClick={() => profileReq(c.user_id, "reject")}
                  className="bg-slate-100 text-slate-800 font-bold px-3 py-2 rounded-lg flex items-center gap-1 hover:bg-slate-200"><XCircle size={16} /> Recusar</button>
              </div>
            </div>
          ))}
          {requests.job_requests.map((j) => (
            <div key={`j-${j.id}`} className="bg-white rounded-2xl p-6 border border-slate-200">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                <div>
                  <span className="bg-blue-100 text-blue-800 text-xs font-extrabold px-3 py-1 rounded-full">Alteração de vaga</span>
                  <h3 className="font-heading text-lg font-extrabold text-slate-950 mt-2">{j.cargo} — {j.company_name}</h3>
                  <p className="text-slate-600 text-sm mt-1">
                    Novo: {j.pending_changes?.cidade} • {j.pending_changes?.tipo} • {j.pending_changes?.salario}
                  </p>
                  <p className="text-slate-500 text-sm mt-1 line-clamp-2">{j.pending_changes?.descricao}</p>
                </div>
                <div className="flex gap-2 self-start">
                  <button data-testid={`job-change-approve-${j.id}`} onClick={() => jobChange(j.id, "approve")}
                    className="bg-emerald-400 text-slate-950 font-bold px-3 py-2 rounded-lg flex items-center gap-1 hover:bg-emerald-500"><CheckCircle2 size={16} /> Aprovar</button>
                  <button data-testid={`job-change-reject-${j.id}`} onClick={() => jobChange(j.id, "reject")}
                    className="bg-slate-100 text-slate-800 font-bold px-3 py-2 rounded-lg flex items-center gap-1 hover:bg-slate-200"><XCircle size={16} /> Recusar</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto" data-testid="admin-payments-list">
          <table className="w-full text-left">
            <thead className="bg-slate-50 text-slate-500 text-sm">
              <tr>
                <th className="p-4 font-bold">Empresa</th><th className="p-4 font-bold">Plano</th>
                <th className="p-4 font-bold">Valor</th><th className="p-4 font-bold">Status</th><th className="p-4 font-bold">Data</th>
              </tr>
            </thead>
            <tbody>
              {payments.length === 0 && <tr><td colSpan={5} className="p-6 text-slate-500">Nenhum pagamento ainda.</td></tr>}
              {payments.map((p) => (
                <tr key={p.order_id} className="border-t border-slate-100">
                  <td className="p-4 font-semibold">{p.company_name}</td>
                  <td className="p-4">{p.plan_title}</td>
                  <td className="p-4 font-bold">{BRL(p.amount)}</td>
                  <td className="p-4">
                    <span className={`text-xs font-extrabold px-3 py-1 rounded-full ${p.status === "paid" ? "bg-emerald-100 text-emerald-800" : p.status === "rejected" ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"}`}>
                      {p.status === "paid" ? "Pago" : p.status === "rejected" ? "Recusado" : "Pendente"}
                    </span>
                  </td>
                  <td className="p-4 text-slate-500 text-sm">{new Date(p.created_at).toLocaleDateString("pt-BR")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
